import React from 'react';
import numeral from 'numeral';
import classNames from 'classnames';
import style from 'assets/jss/material-dashboard-pro-react/components/fbbTableSummary';
import { makeStyles } from '@material-ui/core';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import { BodyMBold, BodySBold, Caption } from 'components/Typography/Typography';
import { translate } from 'components/Translation';
import Button from 'components/CustomButtons/Button';
import Create from '@material-ui/icons/Create';
import Delete from '@material-ui/icons/DeleteOutline';
import { countPlan } from 'views/AddNewServices/AddSingleService/helpers/counter';

// @ts-ignore
const useStyle = makeStyles(style);

interface ISummaryProps {
	plan: types.services.fixed.IPlan;
	title: string;
	subtitle: string;
	detailsTitle?: string;
	additionalServices: types.services.fixed.IAdditionalService[];
	requiredService: types.services.fixed.IRequiredService;
	onEdit?: () => void;
	onDelete?: () => void;
	showDetails?: boolean;
}

const Summary = (props: ISummaryProps) => {
	const {
		plan,
		title,
		subtitle,
		detailsTitle,
		additionalServices,
		requiredService,
		showDetails,
		onEdit,
		onDelete,
	} = props;

	const classes = useStyle();
	return (
		<div className={classes.summaryContainer}>
			<div className={classes.summary}>
				<div className={classes.header}>
					<BodyMBold color="primary-dark" disableMargin>
						{title}
					</BodyMBold>
					<div className={classes.subheader}>
						<BodySBold color="disabled-dark" disableMargin>
							{subtitle}
						</BodySBold>
					</div>
				</div>
				<div className={classes.content}>
					<div className={classes.flex}>
						<ConditionalRender
							show={!!detailsTitle}
							onTrue={() => (
								<div className={classes.flex1}>
									{React.createElement(
										showDetails ? Caption : BodySBold,
										{ color: 'primary-light' },
										detailsTitle
									)}
								</div>
							)}
						/>
						<ConditionalRender
							show={!!showDetails}
							onTrue={() => (
								<div className={classes.servicePrice}>
									<BodyMBold disableMargin color="disabled-light">
										{translate('add.new.line.price', numeral(plan.fee).format('0.000'))}
									</BodyMBold>
								</div>
							)}
						/>
					</div>
					<Caption color="disabled-light" disableMargin>
						{requiredService?.name}
					</Caption>
					<ConditionalRender
						show={additionalServices.length > 0}
						onTrue={() => (
							<div className={classes.additionalServices}>
								<ConditionalRender
									show={!!showDetails}
									onTrue={() => (
										<div className={classNames(classes.flex, classes.servicesHeader)}>
											<div className={classes.flex1}>
												<Caption color="disabled-light" disableMargin>
													{translate('add.new.line.fixed.summary.services.header.service')}
												</Caption>
											</div>
											<div className={classNames(classes.flex1, classes.alignRight)}>
												<Caption color="disabled-light" disableMargin>
													{translate('add.new.line.fixed.summary.services.header.oneTimeFee')}
												</Caption>
											</div>
											<div className={classNames(classes.flex1, classes.alignRight)}>
												<Caption color="disabled-light" disableMargin>
													{translate('add.new.line.fixed.summary.services.header.monthlyFee')}
												</Caption>
											</div>
										</div>
									)}
								/>
								{additionalServices.map((service) => {
									return (
										<div className={classes.additionalService} key={service.uniqueID}>
											<div className={classes.serviceName}>
												<Caption
													color={!!showDetails ? 'secondary-light' : 'disabled-light'}
													disableMargin
												>
													{service.name}
												</Caption>
											</div>
											<div className={classes.servicePrice}>
												<Caption
													color={!!showDetails ? 'secondary-light' : 'disabled-light'}
													disableMargin
												>
													{translate(
														'add.new.line.price',
														numeral(service.oneTimeFee).format('0.000')
													)}
												</Caption>
											</div>
											<ConditionalRender
												show={!!showDetails}
												onTrue={() => (
													<div className={classes.servicePrice}>
														<Caption
															color={!!showDetails ? 'secondary-light' : 'disabled-light'}
															disableMargin
														>
															{translate(
																'add.new.line.price',
																numeral(service.monthlyFee).format('0.000')
															)}
														</Caption>
													</div>
												)}
											/>
										</div>
									);
								})}
							</div>
						)}
					/>
					<ConditionalRender
						show={!!showDetails}
						onTrue={() => (
							<div className={classNames(classes.flex, classes.monthlyCost)}>
								<div className={classNames(classes.flex1, classes.alignEnd)}>
									<Caption color="primary-light" disableMargin>
										{translate('add.new.line.fixed.summary.monthlyCost')}
									</Caption>
								</div>
								<div className={classNames(classes.flex1, classes.alignRight)}>
									<BodyMBold disableMargin color="primary-light">
										<>{countPlan(plan, additionalServices)} OMR</>
									</BodyMBold>
								</div>
							</div>
						)}
					/>
				</div>
			</div>
			<div className={classes.buttonsContainer}>
				<ConditionalRender
					show={!!onEdit}
					onTrue={() => (
						<Button color="tertiary-light" justIcon onClick={() => onEdit!()}>
							<Create />
						</Button>
					)}
				/>
				<ConditionalRender
					show={!!onDelete}
					onTrue={() => (
						<Button color="tertiary-light" justIcon onClick={() => onDelete!()}>
							<Delete />
						</Button>
					)}
				/>
			</div>
		</div>
	);
};

export default Summary;
